import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import {
  isAccessRole,
  isTeamAccessExpired,
  namedAsPropertyManager,
  teamLevel as teamLevelOf,
} from '@/lib/projectTeam';

/**
 * What the signed-in user may do, globally and on one project.
 *
 * Five global tiers, matching app_user.role:
 *
 *   admin            the Construction Manager. Everything, plus user
 *                    administration - the only tier that can change roles.
 *   project_manager  every project, full edit, may archive and close out.
 *   vp_property      the VP of Property Management (migration 026). Every
 *                    project to see; edits only those naming them as
 *                    Property Manager - the Property Manager box, the Senior
 *                    Property Manager box, or a current Property Manager
 *                    entry on the Team tab - and there, a Property Manager's
 *                    rights. Named any other way is read only.
 *   viewer           every project, read only. Sees the whole portfolio and
 *                    changes no part of it.
 *   user             Property Managers and APMs: only the projects naming
 *                    them, and never the closing-out actions.
 *
 * Note that `viewer` is not simply a weaker `project_manager`: it has the
 * widest reach of any tier and no write rights at all. Anything that decides
 * whether to show an editing control must therefore test `canEditProject` and
 * friends, never `canViewProject`.
 *
 * Assignment grants the running of a project, not the setting of its
 * terms: `canEditProjectDetails` is the two administrator tiers only, and
 * the API enforces the same rule field by field. The one thing assignment
 * does carry is a tenant project's schedule - `canEditProjectDates`.
 *
 * A `user` is assigned to a project by being named in one of its five
 * assignment fields - property_manager, senior_property_manager (since
 * migration 021), apm, construction_manager or project_manager_name - which
 * is what the New Project form fills in. Members of the project's `team` array
 * (the Team tab) with a Westwood role count as assigned too; a Contractor or
 * Tenant there is a contact record and does not.
 *
 * These flags decide what is shown. They are not the security boundary: the
 * API enforces the same rules in SQL, so a hidden button is a courtesy, not a
 * lock. See the hard rules in CLAUDE.md.
 */

const ASSIGNMENT_FIELDS = [
  'property_manager',
  'senior_property_manager',
  'apm',
  'construction_manager',
  'project_manager_name',
];

export function useProjectRole(project) {
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me(),
    staleTime: 60_000,
  });

  const isAdmin = currentUser?.role === 'admin';
  const isProjectAdmin = isAdmin || currentUser?.role === 'project_manager';
  const isViewer = currentUser?.role === 'viewer';
  const isVpProperty = currentUser?.role === 'vp_property';
  /* Reach, kept separate from rights - see the note above. */
  const canSeeAllProjects = isProjectAdmin || isVpProperty || isViewer;
  const userEmail = currentUser?.email?.toLowerCase();

  /* Which of the four fields names this user, if any. Surfaced so a screen can
     say "you are the APM here" rather than only "you have access". */
  const assignment = project && userEmail
    ? ASSIGNMENT_FIELDS.find(f => project[f]?.toLowerCase() === userEmail) || null
    : null;

  /* This user's Westwood role on the Team tab, if any. A Contractor or Tenant
     entry is a contact record and opens nothing, and nor does an entry whose
     end date has passed - the API already withholds the project, so this only
     keeps the two in step. */
  const projectRole = project && userEmail
    ? (project.team || []).find(m => m.email?.toLowerCase() === userEmail
        && isAccessRole(m.role) && !isTeamAccessExpired(m))?.role || null
    : null;

  /* Their level on this project, for the Team tab: who they may add. */
  const teamLevel = teamLevelOf(project, currentUser);

  const isArchived = !!project?.archived;
  const isAssigned = isProjectAdmin || !!assignment || !!projectRole;

  /* Archived is read-only for everyone below project administrator: the people
     who ran a project keep being able to look it up, but not to change it.

     A viewer is refused up front rather than folded into the expression: one
     may well be named on a project as its Property Manager, and that naming
     must not become a way to edit. The role wins over the assignment.

     The VP of Property Management is the same idea, narrower: they see every
     project, and only being its Property Manager makes one theirs to edit -
     not being its APM, its creator or on its Team tab in another role. The
     API's PROJECT_WRITABLE asks the same question. */
  const editsHere = isVpProperty ? namedAsPropertyManager(project, currentUser) : isAssigned;
  const canWrite = !isViewer && (isProjectAdmin || (editsHere && !isArchived));

  /* Every Westwood Team tab role may do everything a named person may
     (Evan, 2026-10-16), so being able to write is the whole question. The
     API's `teamRoles` table in api/src/lib/entities.ts says the same; if it
     ever narrows a role again, narrow it here too. */
  const canEditProject = canWrite;

  /* A tenant project's schedule is a special case of canEditProjectDetails.
     Its dates move with the tenant - the lease, rent commencement, the plans,
     the deadline to open - not with the construction schedule, and the Property
     Manager is the one hearing about it first; the TI allowance travels with
     the same conversation. So on a tenant project the dates and the allowance
     go back to whoever is running it. Capital and MOE keep theirs with the
     Construction Manager, and the budget is nobody else's on any type.

     The API enforces exactly this, per field and per project type, in
     `managerOnlyExemptions`. */
  const canEditProjectDates = isProjectAdmin || (canEditProject && project?.type === 'tenant');

  /* An existing tenant's alteration (migration 024) is opened by the Property
     Manager, so what it is and where it is - name, number, property, suite,
     description - are theirs to correct, along with the tenant's insurance.
     Who runs it, the dates and the money stay with canEditProjectDetails.
     The API's `managerOnlyExemptions` releases exactly these. */
  const canEditProjectBasics = isProjectAdmin || (canEditProject && project?.type === 'tenant_alteration');

  return {
    isAdmin,
    isProjectAdmin,
    isViewer,
    isVpProperty,
    canSeeAllProjects,
    assignment,
    projectRole,
    currentUser,
    isArchived,
    isLoaded: !userLoading && !!currentUser,

    /* Global - do not depend on which project is open. */
    canCreateProject:   isProjectAdmin,
    /* An existing tenant's alteration is the one project anyone who may
       write opens for themselves (migration 024): the API's `createRelease`
       on the Project entity. */
    canOpenAlteration:  !!currentUser && !isViewer,
    canManageUsers:     isAdmin,

    /* Reserved to the Construction Manager and the Project Manager. */
    canArchiveProject:  isProjectAdmin,
    /* The project's own details: what it is, where it is, who runs it,
       when it starts and what it costs. Someone assigned to a project
       still runs it - status, updates, tasks, milestones, plans, files -
       but the terms it was set up on are not theirs to move. Kept apart
       from canEditProject, which also gates the daily reports, the
       workflow tab and the overview: narrowing that one would switch off
       far more than the project form. */
    canEditProjectDetails: isProjectAdmin,
    canCompleteProject: isProjectAdmin,
    canDeleteProject:   isAdmin,

    /* This project. */
    canViewProject:      canSeeAllProjects || isAssigned,
    canEditProject,
    /* Depends on this project's type - see the note above. */
    canEditProjectDates,
    canEditProjectBasics,
    /* Adding people needs a level - APM or above on this project - and then
       only at that level or below; the Team tab narrows the list. */
    teamLevel,
    canManageTeam:       canWrite && (isProjectAdmin || teamLevel >= 2),
    canCreatePlan:       canWrite,
    canUploadFiles:      canWrite,
    canDeleteFiles:      canWrite,
    canReview:           canWrite,
    canViewDraftPlans:   canSeeAllProjects || isAssigned,
    canManageMilestones: canWrite,
    canUpdateTaskStatus: canWrite,
  };
}
